import { isNonEmpty } from '@sgme/fp';
import { Collapse } from '@sgme/ui';
import { FormatSectionTitleFn } from 'components/common-form/sidePanel/AccordionSection';
import type { GreeksFeesMetricsProps } from 'components/common-form/sidePanel/info/QuoteInfos/GreeksFeesMetricsTabContent/GreeksFeesMetricsTabContent.connect';
import { ConnectedFees } from 'components/shared/Sensitivity/ConnectedFees';
import { ConnectedGreeks } from 'components/shared/Sensitivity/ConnectedGreeks';
import { ConnectedMetrics } from 'components/shared/Sensitivity/ConnectedMetrics';
import { GenericGrid } from 'components/shared/Sensitivity/Greeks/GenericTable';
import { IntlKey } from 'locales';
import { upperFirst, without } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { NonUniqueSectionItem } from './NonUniqueSectionItem';
import { UniqueSectionItem } from './UniqueSectionItem';

export function GreeksFeesMetricsTabContentPresentation({
  collapsedSections,
  hasFees,
  hasGreeks,
  ficcGreeks,
  hasMetrics,
  greeks,
  toggleSectionVisibility,
  quotesFeesDetailLink,
  tabId,
  workspaceType,
  sensitivities,
  precisionGreeks,
}: GreeksFeesMetricsProps) {
  const toggle = useCallback(
    (name: string) => {
      toggleSectionVisibility(name);
    },
    [toggleSectionVisibility],
  );
  const hasMultipleSections =
    Number(hasFees) + Number(hasGreeks) + Number(hasMetrics) + Number(sensitivities?.length ?? 0) > 1;
  const hasOnlyOneSection = !hasMultipleSections;
  const isFiccGreeks = isNonEmpty(ficcGreeks);
  const greeksSection = <ConnectedGreeks formId={tabId} workspace={workspaceType} />;
  const feesSection = (
    <ConnectedFees formId={tabId} workspace={workspaceType}>
      {quotesFeesDetailLink && (
        <a className="btn btn-flat-secondary btn-icon-end py-2" href={quotesFeesDetailLink!} target="_blank">
          <FormattedMessage id="details" />
          <i className="icon ps-1">download</i>
        </a>
      )}
    </ConnectedFees>
  );
  const metricsSection = <ConnectedMetrics formId={tabId} workspace={workspaceType} />;

  const selected: string[] = useMemo(() => {
    const allSection = sensitivities?.map(sensitivity => sensitivity.name) ?? [];
    if (hasGreeks) {
      allSection.push('greeks');
    }
    if (hasFees) {
      allSection.push('fees');
    }
    if (hasMetrics) {
      allSection.push('metrics');
    }

    return without(allSection, ...collapsedSections);
  }, [hasGreeks, hasFees, hasMetrics, sensitivities, collapsedSections]);

  const onSelectAccordionItem = (itemKey: string) => {
    toggle(itemKey);
  };

  const getTitle: FormatSectionTitleFn<string> = (key, formatMsg) =>
    formatMsg(`quoteDetails.Tab.${upperFirst(key)}` as IntlKey);

  return (
    <Collapse
      activeKey={selected}
      onSelect={k => onSelectAccordionItem(k as string)}
      fill="fill"
      icon="end"
      size="sm"
      className="overflow-y-auto mt-4 px-3 flex-grow-1"
    >
      <>
        {sensitivities &&
          sensitivities.map(sensitivity => (
            <>
              {hasOnlyOneSection && (
                <UniqueSectionItem
                  getTitle={getTitle}
                  content={
                    <GenericGrid data={sensitivity} precision={precisionGreeks} copyActionName="Copy sensitivities" />
                  }
                  sectionName={sensitivity.name}
                />
              )}
              {hasMultipleSections && (
                <NonUniqueSectionItem getTitle={getTitle} sectionName={sensitivity.name}>
                  <GenericGrid data={sensitivity} precision={precisionGreeks} copyActionName="Copy sensitivities" />
                </NonUniqueSectionItem>
              )}
            </>
          ))}
      </>
      {hasGreeks && hasOnlyOneSection && (
        <UniqueSectionItem getTitle={getTitle} content={greeksSection} sectionName="greeks" />
      )}
      {hasGreeks && hasMultipleSections && greeks && (
        <NonUniqueSectionItem getTitle={getTitle} sectionName="greeks" noModal={isFiccGreeks}>
          {greeksSection}
        </NonUniqueSectionItem>
      )}
      {hasFees && hasOnlyOneSection && (
        <UniqueSectionItem getTitle={getTitle} content={feesSection} sectionName="fees" />
      )}
      {hasFees && hasMultipleSections && (
        <NonUniqueSectionItem getTitle={getTitle} sectionName="fees">
          {feesSection}
        </NonUniqueSectionItem>
      )}
      {hasMetrics && hasOnlyOneSection && (
        <UniqueSectionItem getTitle={getTitle} content={metricsSection} sectionName="metrics" />
      )}
      {hasMetrics && hasMultipleSections && (
        <NonUniqueSectionItem getTitle={getTitle} sectionName="metrics">
          {metricsSection}
        </NonUniqueSectionItem>
      )}
    </Collapse>
  );
}

GreeksFeesMetricsTabContentPresentation.displayName = 'GreeksFeesMetricsTabContentPresentation';
